import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, FileText, MessageSquare, FolderOpen, CheckSquare, Calendar, BookOpen, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to Your Client Portal',
    description: 'We\'re glad to have you here. This portal is your secure space to stay informed about your case, communicate with our team, and access important documents.',
    icon: null,
    image: '/logo.png',
    tips: [
      'Access your portal anytime, anywhere',
      'All your case information in one place',
      'Secure and confidential communication'
    ]
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'Your dashboard is your home base. Here you\'ll see an overview of your active cases, pending tasks, upcoming appointments, and recent messages.',
    icon: FileText,
    color: 'bg-purple-100 text-[#80298F]',
    tips: [
      'View all your active cases at a glance',
      'See pending tasks that need your attention',
      'Check unread messages from your legal team'
    ]
  },
  {
    id: 'cases',
    title: 'Track Your Case Status',
    description: 'Click on any case to see detailed status updates. We keep you informed at every step of the legal process with clear explanations of what\'s happening and what comes next.',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600',
    tips: [
      'See your current case status and progress',
      'Understand what each status means',
      'Track milestones achieved in your case'
    ]
  },
  {
    id: 'messages',
    title: 'Secure Messaging',
    description: 'Communicate directly with your legal team through our secure messaging system. All conversations are private and linked to your specific case.',
    icon: MessageSquare,
    color: 'bg-green-100 text-green-600',
    tips: [
      'Send messages to your attorney anytime',
      'Attach files to your messages',
      'All messages are stored securely'
    ]
  },
  {
    id: 'documents',
    title: 'Document Center',
    description: 'Access and download important documents related to your case. You can also upload documents that your legal team requests.',
    icon: FolderOpen,
    color: 'bg-amber-100 text-amber-600',
    tips: [
      'Download court filings and legal documents',
      'Upload requested documents securely',
      'All documents organized by case'
    ]
  },
  {
    id: 'tasks',
    title: 'Your Tasks',
    description: 'Stay on top of action items assigned to you. Complete tasks on time to help your case progress smoothly.',
    icon: CheckSquare,
    color: 'bg-red-100 text-red-600',
    tips: [
      'View tasks assigned to you',
      'See due dates and priorities',
      'Upload documents or take action as requested'
    ]
  },
  {
    id: 'calendar',
    title: 'Calendar & Appointments',
    description: 'View all your upcoming appointments, court dates, and important deadlines in one place.',
    icon: Calendar,
    color: 'bg-indigo-100 text-indigo-600',
    tips: [
      'See upcoming appointments and hearings',
      'View important case deadlines',
      'Never miss an important date'
    ]
  },
  {
    id: 'resources',
    title: 'Resource Library',
    description: 'Access helpful documents, videos, and guides to better understand the legal process and your rights.',
    icon: BookOpen,
    color: 'bg-teal-100 text-teal-600',
    tips: [
      'Educational videos and documents',
      'Learn about the legal process',
      'Understand your rights'
    ]
  },
  {
    id: 'support',
    title: 'We\'re Here to Help',
    description: 'Have questions? Our team is here to support you throughout your case. You can reach us through the portal or by phone.',
    icon: Phone,
    color: 'bg-pink-100 text-pink-600',
    tips: [
      'Message us anytime through the portal',
      'Check the Help page for common questions',
      'Call our office during business hours'
    ]
  }
];

function OnboardingWizard({ onComplete, userName }) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#80298F] to-[#9B3BA7] px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-white/80 text-sm">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={handleSkip}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-[#FAA519] transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <div className="text-center mb-6">
            {step.icon ? (
              <div className={`inline-flex p-4 rounded-2xl ${step.color} mb-4`}>
                <step.icon className="h-12 w-12" />
              </div>
            ) : step.image ? (
              <img 
                src={step.image} 
                alt="The Ramirez Legal Group" 
                className="h-20 w-20 mx-auto mb-4 object-contain"
              />
            ) : null}
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isFirstStep ? `Welcome, ${userName?.split(' ')[0] || 'Client'}!` : step.title}
            </h2>
            <p className="text-gray-600 max-w-md mx-auto">
              {step.description}
            </p>
          </div>

          {/* Tips */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#80298F] text-white rounded-full flex items-center justify-center text-xs">
                {step.tips.length}
              </span>
              Key Points
            </h3>
            <ul className="space-y-2">
              {step.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <ArrowRight className="h-4 w-4 text-[#80298F] mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Step Indicators */}
          <div className="flex justify-center gap-1.5 mb-4">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? 'bg-[#80298F] w-6' 
                    : index < currentStep 
                      ? 'bg-[#80298F]/50' 
                      : 'bg-gray-300'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700"
          >
            Skip Tour
          </Button>

          <div className="flex gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-[#80298F] hover:bg-[#6B2278] text-white flex items-center gap-1"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
